import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaShieldAlt,
  FaArrowRight,
  FaEnvelope,
} from "react-icons/fa";
import "./PrivacyPolicyPage.css";

const PrivacyPolicyPage = () => {
  const lastUpdated = "January 2025";

  /* Smooth scroll to top on load */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="staffline-privacy-page">

      {/* ===== HERO ===== */}
      <section className="staffline-privacy-hero">
        <div className="container staffline-privacy-hero-content">
          <span className="staffline-privacy-hero-tag">
            <FaShieldAlt /> Legal
          </span>
          <h1 className="staffline-privacy-hero-title">
            Privacy <span>Policy</span>
          </h1>
          <p className="staffline-privacy-hero-subtitle">
            Your privacy matters to us. This policy explains how StaffLine
            Transport collects, uses, and protects your personal information.
          </p>
          <span className="staffline-privacy-hero-updated">
            Last Updated: {lastUpdated}
          </span>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <section className="staffline-privacy-section">
        <div className="container">
          <div className="staffline-privacy-content">

            {/* 1. Introduction */}
            <div className="staffline-privacy-block">
              <h2>1. Introduction</h2>
              <p>
                StaffLine Transport Ltd. ("we", "our", "us") is committed to
                protecting the privacy of every individual who uses our
                subscription-based staff shuttle services. This Privacy Policy
                describes how we collect, use, store, and share your personal
                information when you subscribe to our service or interact with
                our website.
              </p>
              <p>
                By subscribing to StaffLine or using our website, you agree to
                the practices described in this policy.
              </p>
            </div>

            {/* 2. Information We Collect */}
            <div className="staffline-privacy-block">
              <h2>2. Information We Collect</h2>
              <p>We collect the following types of information:</p>
              <ul>
                <li>
                  <strong>Personal Details:</strong> Full name, phone number,
                  email address, and home address.
                </li>
                <li>
                  <strong>Subscription Information:</strong> Your chosen route,
                  pickup point, assigned seat, and StaffLine ID.
                </li>
                <li>
                  <strong>Payment Information:</strong> Payment method,
                  transaction reference, and billing history (we do not store
                  card details).
                </li>
                <li>
                  <strong>Usage Data:</strong> Ride history, attendance, and
                  route usage for operational and reporting purposes.
                </li>
                <li>
                  <strong>Communication Data:</strong> SMS and email
                  correspondence between you and StaffLine.
                </li>
              </ul>
            </div>

            {/* 3. How We Use Your Information */}
            <div className="staffline-privacy-block">
              <h2>3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul>
                <li>Provide, manage, and operate your subscription</li>
                <li>Assign your seat, bus, and pickup point</li>
                <li>Send SMS and email notifications about your route</li>
                <li>Process payments and issue receipts</li>
                <li>Improve service quality and customer support</li>
                <li>Comply with legal obligations and safety requirements</li>
              </ul>
            </div>

            {/* 4. Sharing Your Information */}
            <div className="staffline-privacy-block">
              <h2>4. Sharing Your Information</h2>
              <p>
                We do <strong>not</strong> sell your personal data. We may
                share limited information with:
              </p>
              <ul>
                <li>
                  <strong>Payment Processors:</strong> Paystack, Flutterwave,
                  or mobile money providers to handle payments.
                </li>
                <li>
                  <strong>Corporate Clients:</strong> If your subscription is
                  sponsored by your employer, they will see your name, route,
                  and attendance records.
                </li>
                <li>
                  <strong>Legal Authorities:</strong> When required by law or
                  to protect the safety of our passengers and staff.
                </li>
              </ul>
            </div>

            {/* 5. Data Security */}
            <div className="staffline-privacy-block">
              <h2>5. Data Security</h2>
              <p>
                We take reasonable measures to protect your information,
                including encrypted storage, restricted access, and secure
                transmission. However, no digital system is 100% secure — please
                keep your StaffLine ID and account credentials confidential.
              </p>
            </div>

            {/* 6. Your Rights */}
            <div className="staffline-privacy-block">
              <h2>6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li>Request access to the personal data we hold about you</li>
                <li>Request corrections to inaccurate information</li>
                <li>Request deletion of your data (subject to legal obligations)</li>
                <li>Opt out of marketing communications at any time</li>
                <li>Withdraw from the service with reasonable notice</li>
              </ul>
            </div>

            {/* 7. Cookies & Analytics */}
            <div className="staffline-privacy-block">
              <h2>7. Cookies & Analytics</h2>
              <p>
                Our website may use cookies and third-party analytics tools
                (such as Google Analytics) to understand how visitors use our
                site. You can disable cookies in your browser settings, though
                some features may not work correctly.
              </p>
            </div>

            {/* 8. Data Retention */}
            <div className="staffline-privacy-block">
              <h2>8. Data Retention</h2>
              <p>
                We retain your subscription data for as long as you are an
                active subscriber and for a reasonable period after your
                subscription ends, to comply with financial and legal
                obligations.
              </p>
            </div>

            {/* 9. Changes to This Policy */}
            <div className="staffline-privacy-block">
              <h2>9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we
                do, we will update the "Last Updated" date above. Continued use
                of our service means you accept the updated policy.
              </p>
            </div>

            {/* 10. Contact Us */}
            <div className="staffline-privacy-block staffline-privacy-block-contact">
              <h2>10. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or how we
                handle your data, please contact us:
              </p>
              <div className="staffline-privacy-contact-card">
                <a href="mailto:info@stafflinetransport.com">
                  <FaEnvelope />
                  stafflinetransportgh@gmail.com
                </a>
              </div>
            </div>

            {/* Back to Home CTA */}
            <div className="staffline-privacy-back">
              <Link to="/" className="staffline-privacy-back-btn">
                Back to Home <FaArrowRight />
              </Link>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default PrivacyPolicyPage;